import { isAxiosError } from "axios";
import { useCallback } from "react";
import { useToast } from "~/shared/ui/toast";

export interface MutationErrorFallback {
  title: string;
  content: string;
}

// Django REST 응답에서 사용자에게 보여줄 메시지 1개를 뽑는다.
// 1) {detail: "..."} (권한/일반 에러)
// 2) {non_field_errors: ["..."]} (시리얼라이저 레벨)
// 3) {<field>: ["..."]} (필드 검증)
// 우선순위는 위 순서. 어느 것도 못 찾으면 undefined.
function extractBackendMessage(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const obj = data as Record<string, unknown>;

  if (typeof obj.detail === "string" && obj.detail.trim().length > 0) {
    return obj.detail;
  }

  const nonField = obj.non_field_errors;
  if (Array.isArray(nonField)) {
    const first = nonField.find((v): v is string => typeof v === "string" && v.trim().length > 0);
    if (first) return first;
  }

  for (const key of Object.keys(obj)) {
    if (key === "detail" || key === "non_field_errors") continue;
    const value = obj[key];
    if (Array.isArray(value)) {
      const first = value.find((v): v is string => typeof v === "string" && v.trim().length > 0);
      if (first) return first;
    } else if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }

  return undefined;
}

// mutation 에러 응답에서 BE 메시지를 추출해 토스트로 노출하는 공통 헬퍼.
// 4xx + 추출 가능한 user-facing 메시지가 있을 때만 사용자에게 보여주고,
// 그 외(5xx / 추출 실패 / 네트워크 에러)는 fallback 으로 떨어진다.
export function useMutationErrorToast() {
  const { showToast } = useToast();
  return useCallback(
    (error: unknown, fallback: MutationErrorFallback) => {
      let content = fallback.content;
      if (isAxiosError(error)) {
        const status = error.response?.status ?? 0;
        if (status >= 400 && status < 500) {
          const backendMessage = extractBackendMessage(error.response?.data);
          if (backendMessage) {
            content = backendMessage;
          }
        }
      }
      showToast({ type: "error", title: fallback.title, content });
    },
    [showToast],
  );
}
